import React from 'react';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import type { SectionEntry } from './config-accordion';
import { FieldDisplay } from './pick-display';

interface GridLayoutProps {
  data: SectionEntry[];
  getFieldLabel: (key: string) => string;
}

// Grid layout for images/videos — 2x2 cards each rendered through the field-type display.
export const ImageGridLayout: React.FC<GridLayoutProps> = ({ data, getFieldLabel }) => {
  const { fontSize } = useResponsiveSizes();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
      {data.map(({ key, value, field }) => (
        <div
          key={key}
          style={{
            padding: 12,
            background: 'white',
            borderRadius: 6,
            border: '1px solid #e2e8f0',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: fontSize.sm, fontWeight: 500, marginBottom: 8, color: '#64748b' }}>
            {getFieldLabel(key)}
          </div>
          <FieldDisplay field={field} value={value} />
        </div>
      ))}
    </div>
  );
};

// Region/time grouper: collapses regionN/timeN pairs into a single tile.
export const RegionGridLayout: React.FC<GridLayoutProps> = ({ data }) => {
  const { fontSize, spacing } = useResponsiveSizes();

  const regionMap = new Map<string, Record<string, unknown>>();

  data.forEach(({ key, value }) => {
    const match = key.match(/region(\d+)|time(\d+)/);
    if (match) {
      const num = match[1] || match[2];
      if (!regionMap.has(num)) regionMap.set(num, {});
      regionMap.get(num)![key] = value;
    }
  });

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: spacing.sm
      }}
    >
      {Array.from(regionMap.entries()).map(([num, fields]) => (
        <div
          key={num}
          style={{
            padding: spacing.sm,
            background: 'white',
            borderRadius: 6,
            border: '1px solid #e2e8f0'
          }}
        >
          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-around',
              fontSize: fontSize.base,
              fontWeight: 500
            }}
          >
            {fields[`region${num}`] !== undefined && <div>📍 {String(fields[`region${num}`])}</div>}
            {fields[`time${num}`] !== undefined && <div>🕐 {String(fields[`time${num}`])}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

// True when every entry is an image/video field in the template.
// Falls back to key-name matching when no FormField is available.
export const isImageGrid = (data: SectionEntry[]): boolean =>
  data.every(({ key, field }) => {
    if (field) return field.type === 'image' || field.type === 'video';
    return key.includes('image') || key.includes('video');
  });
